import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';

interface Appointment {
  id: string;
  applicant_name: string;
  email: string;
  phone: string;
  appointment_date: string;
  is_lagos: boolean;
  physical_address: string | null;
  zoom_link: string | null;
  reminder_24hr_sent: boolean;
  reminder_evening_sent: boolean;
  reminder_2hr_sent: boolean;
  attendance_status: 'pending' | 'attended' | 'no_show';
  created_at: string;
}

type ReminderType = '24hr' | 'evening' | '2hr' | 'anytime' | 'followup';
type ViewMode = 'upcoming' | 'past';
type AppointmentSection = 'zoom' | 'in-person';

export default function RemindersTab() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState<string | null>(null);
  const [bulkSending, setBulkSending] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<{ [key: string]: ReminderType }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('upcoming');
  const [activeSection, setActiveSection] = useState<AppointmentSection>('zoom');
  const [customMessages, setCustomMessages] = useState<{ [key: string]: string }>({});
  const [expandedMessage, setExpandedMessage] = useState<{ [key: string]: boolean }>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkReminderType, setBulkReminderType] = useState<ReminderType>('anytime');
  const [updatingAttendance, setUpdatingAttendance] = useState<string | null>(null);
  const [bulkSendResult, setBulkSendResult] = useState<{ success: number; failed: number } | null>(null);
  const [bulkCustomMessage, setBulkCustomMessage] = useState('');

  useEffect(() => {
    fetchAllAppointments();
  }, []);

  // Clear selections when view/section changes
  useEffect(() => {
    setSelectedIds(new Set());
    setBulkSendResult(null);
  }, [viewMode, activeSection, searchQuery]);

  const fetchAllAppointments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('appointments')
      .select('*')
      .order('appointment_date', { ascending: true });

    if (error) {
      console.error('Error fetching appointments:', error);
    } else {
      const all = (data || []) as Appointment[];
      setAppointments(all);
      const initialSelection: { [key: string]: ReminderType } = {};
      all.forEach(app => { initialSelection[app.id] = '24hr'; });
      setSelectedReminder(initialSelection);
    }
    setLoading(false);
  };

  const now = new Date();

  const filteredAppointments = appointments.filter(app => {
    const appDate = new Date(app.appointment_date);
    const isUpcoming = appDate >= now;
    const matchesView = viewMode === 'upcoming' ? isUpcoming : !isUpcoming;
    const matchesSearch = app.applicant_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesView && matchesSearch;
  });

  const zoomAppointments = filteredAppointments.filter(a => !a.is_lagos);
  const inPersonAppointments = filteredAppointments.filter(a => a.is_lagos);
  const displayedAppointments = activeSection === 'zoom' ? zoomAppointments : inPersonAppointments;

  // ── Attendance ──────────────────────────────────────────────────────────────
  const updateAttendance = async (appointmentId: string, status: 'attended' | 'no_show' | 'pending') => {
    setUpdatingAttendance(appointmentId);
    const { error } = await supabase
      .from('appointments')
      .update({ attendance_status: status })
      .eq('id', appointmentId);

    if (!error) {
      setAppointments(prev =>
        prev.map(app => app.id === appointmentId ? { ...app, attendance_status: status } : app)
      );
    }
    setUpdatingAttendance(null);
  };

  // ── Selection helpers ────────────────────────────────────────────────────────
  const toggleSelectAll = () => {
    if (selectedIds.size === displayedAppointments.length && displayedAppointments.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedAppointments.map(a => a.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const allSelected = displayedAppointments.length > 0 && selectedIds.size === displayedAppointments.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  // ── Bulk Send ────────────────────────────────────────────────────────────────
  const sendBulkReminders = async () => {
    if (selectedIds.size === 0) return;

    if (bulkReminderType === 'followup' && !bulkCustomMessage.trim()) {
      alert('⚠️ Please type a follow-up message before sending to all.');
      return;
    }

    setBulkSending(true);
    setBulkSendResult(null);

    const targets = displayedAppointments.filter(a => selectedIds.has(a.id));
    let success = 0;
    let failed = 0;

    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

    for (const appointment of targets) {
      try {
        const response = await fetch(`${supabaseUrl}/functions/v1/schedule-reminder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({
            action: 'manual_reminder',
            appointmentId: appointment.id,
            reminderType: bulkReminderType,
            customMessage: bulkCustomMessage.trim() || undefined,
          }),
        });
        const result = await response.json();
        if (response.ok && result.success) {
          success++;
          setAppointments(prev =>
            prev.map(app => {
              if (app.id !== appointment.id) return app;
              return {
                ...app,
                reminder_24hr_sent: bulkReminderType === '24hr' ? true : app.reminder_24hr_sent,
                reminder_evening_sent: bulkReminderType === 'evening' ? true : app.reminder_evening_sent,
                reminder_2hr_sent: bulkReminderType === '2hr' ? true : app.reminder_2hr_sent,
              };
            })
          );
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }

    setBulkSendResult({ success, failed });
    setBulkSending(false);
    setSelectedIds(new Set());
  };

  // ── Individual Send ──────────────────────────────────────────────────────────
  const sendManualReminder = async (appointmentId: string) => {
    const appointment = appointments.find(a => a.id === appointmentId);
    if (!appointment) return;

    const reminderType = selectedReminder[appointmentId] || '24hr';
    const customMessage = customMessages[appointmentId] || '';

    if (reminderType === 'followup' && !customMessage.trim()) {
      alert('⚠️ Please type a follow-up message before sending.');
      return;
    }

    setSendingReminder(appointmentId);

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/schedule-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          action: 'manual_reminder',
          appointmentId: appointment.id,
          reminderType,
          customMessage: customMessage.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setAppointments(prev =>
          prev.map(app => {
            if (app.id !== appointmentId) return app;
            return {
              ...app,
              reminder_24hr_sent: reminderType === '24hr' ? true : app.reminder_24hr_sent,
              reminder_evening_sent: reminderType === 'evening' ? true : app.reminder_evening_sent,
              reminder_2hr_sent: reminderType === '2hr' ? true : app.reminder_2hr_sent,
            };
          })
        );
        alert(`✅ ${getReminderLabel(reminderType)} sent to ${appointment.applicant_name}!`);
      } else {
        alert(`❌ Failed to send: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending manual reminder:', error);
      alert('❌ Failed to send. Please try again.');
    } finally {
      setSendingReminder(null);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getReminderLabel = (type: ReminderType): string => {
    switch (type) {
      case '24hr': return '24-Hour Before';
      case 'evening': return 'Evening Before';
      case '2hr': return '2-Hour Before';
      case 'anytime': return 'Send Anytime (General)';
      case 'followup': return 'Custom Follow-Up';
      default: return '';
    }
  };

  const getReminderStatus = (app: Appointment, type: ReminderType): boolean => {
    switch (type) {
      case '24hr': return app.reminder_24hr_sent;
      case 'evening': return app.reminder_evening_sent;
      case '2hr': return app.reminder_2hr_sent;
      default: return false;
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-NG', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      timeZone: 'Africa/Lagos',
    });

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString('en-NG', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZone: 'Africa/Lagos',
    });

  const toggleMessageExpand = (id: string) => {
    setExpandedMessage(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // ── Attendance Badge ─────────────────────────────────────────────────────────
  const AttendanceBadge = ({ app }: { app: Appointment }) => {
    const status = app.attendance_status || 'pending';
    const isUpdating = updatingAttendance === app.id;

    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Attendance</span>
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => updateAttendance(app.id, 'attended')}
            disabled={isUpdating}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all border ${
              status === 'attended'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                : 'bg-white text-stone-500 border-stone-300 hover:border-emerald-400 hover:text-emerald-600'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isUpdating && status !== 'attended' ? (
              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-xs"></i>
              </div>
            )}
            Attended
          </button>
          <button
            onClick={() => updateAttendance(app.id, 'no_show')}
            disabled={isUpdating}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all border ${
              status === 'no_show'
                ? 'bg-red-500 text-white border-red-500 shadow-sm'
                : 'bg-white text-stone-500 border-stone-300 hover:border-red-400 hover:text-red-500'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isUpdating && status !== 'no_show' ? (
              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-close-circle-line text-xs"></i>
              </div>
            )}
            No-Show
          </button>
          {status !== 'pending' && (
            <button
              onClick={() => updateAttendance(app.id, 'pending')}
              disabled={isUpdating}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs text-stone-400 hover:text-stone-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-refresh-line text-xs"></i>
              </div>
              Reset
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── Message Composer ─────────────────────────────────────────────────────────
  const renderMessageComposer = (app: Appointment) => {
    const reminderType = selectedReminder[app.id] || '24hr';
    const isFollowup = reminderType === 'followup';
    const isExpanded = expandedMessage[app.id] || false;
    const message = customMessages[app.id] || '';
    const MAX_CHARS = 500;

    if (!isFollowup && !isExpanded) {
      return (
        <button
          onClick={() => toggleMessageExpand(app.id)}
          className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-emerald-700 transition-colors cursor-pointer mt-1"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-edit-line text-xs"></i>
          </div>
          Add custom message (optional)
        </button>
      );
    }

    return (
      <div className="mt-2 space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-message-2-line text-xs text-emerald-600"></i>
            </div>
            {isFollowup ? 'Follow-Up Message' : 'Custom Message'}
            {isFollowup && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          {!isFollowup && (
            <button
              onClick={() => toggleMessageExpand(app.id)}
              className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer flex items-center gap-1"
            >
              <i className="ri-close-line text-xs"></i> Hide
            </button>
          )}
        </div>
        <textarea
          value={message}
          onChange={e => {
            if (e.target.value.length <= MAX_CHARS) {
              setCustomMessages(prev => ({ ...prev, [app.id]: e.target.value }));
            }
          }}
          placeholder={
            isFollowup
              ? 'Type your follow-up message here...'
              : 'Add a personal note to include with this reminder...'
          }
          rows={3}
          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none placeholder-stone-400"
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-stone-400">
            {isFollowup ? 'Sent via email & WhatsApp.' : 'Appended to the standard reminder.'}
          </p>
          <span className={`text-xs font-medium ${message.length > MAX_CHARS * 0.9 ? 'text-amber-600' : 'text-stone-400'}`}>
            {message.length}/{MAX_CHARS}
          </span>
        </div>
        {message.trim() && (
          <button
            onClick={() => setCustomMessages(prev => ({ ...prev, [app.id]: '' }))}
            className="text-xs text-stone-400 hover:text-red-500 transition-colors cursor-pointer flex items-center gap-1"
          >
            <i className="ri-delete-bin-line text-xs"></i> Clear message
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Info Banner */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <i className="ri-notification-3-line text-emerald-600 text-xl"></i>
        </div>
        <div>
          <h3 className="font-semibold text-emerald-900 mb-1">Manual Reminder & Follow-Up Control</h3>
          <p className="text-sm text-emerald-700">
            Select applicants individually or use <strong>Select All</strong> to bulk-send reminders at once. For past appointments, mark attendance as <strong>Attended</strong> or <strong>No-Show</strong>.
          </p>
        </div>
      </div>

      {/* Controls Row */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 min-w-0">
          <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <i className="ri-search-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search applicant by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>

        <div className="flex items-center bg-stone-100 rounded-lg p-1 gap-1 flex-shrink-0">
          <button
            onClick={() => setViewMode('upcoming')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
              viewMode === 'upcoming' ? 'bg-white text-emerald-700 shadow-sm font-semibold' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-xs"></i>
              Upcoming
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${viewMode === 'upcoming' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500'}`}>
                {appointments.filter(a => new Date(a.appointment_date) >= now).length}
              </span>
            </span>
          </button>
          <button
            onClick={() => setViewMode('past')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
              viewMode === 'past' ? 'bg-white text-amber-700 shadow-sm font-semibold' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <i className="ri-history-line text-xs"></i>
              Past
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${viewMode === 'past' ? 'bg-amber-100 text-amber-700' : 'bg-stone-200 text-stone-500'}`}>
                {appointments.filter(a => new Date(a.appointment_date) < now).length}
              </span>
            </span>
          </button>
        </div>
      </div>

      {viewMode === 'past' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-history-line text-amber-600 text-sm"></i>
          </div>
          <p className="text-sm text-amber-800">
            Showing past appointments. Mark attendance status and send follow-up messages as needed.
          </p>
        </div>
      )}

      {/* Zoom / In-Person Tabs */}
      <div className="flex gap-3">
        <button
          onClick={() => setActiveSection('zoom')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer border transition-all ${
            activeSection === 'zoom' ? 'bg-stone-800 text-white border-stone-800 shadow' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-video-line text-base"></i>
          </div>
          Zoom Meetings
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${activeSection === 'zoom' ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-600'}`}>
            {filteredAppointments.filter(a => !a.is_lagos).length}
          </span>
        </button>
        <button
          onClick={() => setActiveSection('in-person')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer border transition-all ${
            activeSection === 'in-person' ? 'bg-emerald-700 text-white border-emerald-700 shadow' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-map-pin-line text-base"></i>
          </div>
          In-Person (Lagos)
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${activeSection === 'in-person' ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-600'}`}>
            {filteredAppointments.filter(a => a.is_lagos).length}
          </span>
        </button>
      </div>

      {/* Section Label + Select All + Bulk Send Bar */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-1 h-6 rounded-full ${activeSection === 'zoom' ? 'bg-stone-800' : 'bg-emerald-600'}`}></div>
            <h3 className="font-semibold text-stone-800 text-base">
              {activeSection === 'zoom' ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center"><i className="ri-video-line text-stone-700"></i></div>
                  Zoom Meeting Appointments
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center"><i className="ri-map-pin-line text-emerald-700"></i></div>
                  In-Person Appointments — Lagos
                </span>
              )}
            </h3>
            <span className="text-sm text-stone-500">
              {displayedAppointments.length} {viewMode === 'past' ? 'past' : 'upcoming'} appointment{displayedAppointments.length !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
            </span>
          </div>

          {/* Select All checkbox */}
          {displayedAppointments.length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected; }}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer"
              />
              <span className="text-sm font-medium text-stone-700 whitespace-nowrap">
                {allSelected ? 'Deselect All' : 'Select All'}
              </span>
            </label>
          )}
        </div>

        {/* Bulk Send Panel */}
        {selectedIds.size > 0 && (
          <div className="bg-stone-800 text-white rounded-xl px-5 py-4 flex flex-col gap-4 shadow-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                  <i className="ri-checkbox-multiple-line text-white text-base"></i>
                </div>
                <div>
                  <p className="font-semibold text-sm">{selectedIds.size} selected</p>
                  <p className="text-xs text-stone-300">Bulk send to all selected applicants</p>
                </div>
              </div>

              <div className="flex flex-1 items-center gap-3 flex-wrap">
                <select
                  value={bulkReminderType}
                  onChange={e => setBulkReminderType(e.target.value as ReminderType)}
                  disabled={bulkSending}
                  className="px-3 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/40 cursor-pointer flex-1 min-w-[180px]"
                >
                  <option value="24hr" className="text-stone-900 bg-white">⏰ 24-Hour Before</option>
                  <option value="evening" className="text-stone-900 bg-white">🌙 Evening Before</option>
                  <option value="2hr" className="text-stone-900 bg-white">⚡ 2-Hour Before</option>
                  <option value="anytime" className="text-stone-900 bg-white">📤 Send Anytime (General)</option>
                  <option value="followup" className="text-stone-900 bg-white">✍️ Custom Follow-Up</option>
                </select>

                <button
                  onClick={sendBulkReminders}
                  disabled={bulkSending}
                  className="flex items-center gap-2 px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkSending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-send-plane-fill text-sm"></i>
                      </div>
                      Send to All {selectedIds.size}
                    </>
                  )}
                </button>

                <button
                  onClick={() => setSelectedIds(new Set())}
                  disabled={bulkSending}
                  className="text-stone-300 hover:text-white text-sm cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Bulk message box — always visible in bulk panel */}
            <div className="border-t border-white/10 pt-4 space-y-2">
              <label className="text-xs font-semibold text-stone-300 uppercase tracking-wider flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-message-2-line text-xs text-emerald-400"></i>
                </div>
                {bulkReminderType === 'followup' ? (
                  <>Follow-Up Message <span className="text-red-400 ml-0.5">*</span></>
                ) : (
                  'Add Custom Message (optional)'
                )}
              </label>
              <textarea
                value={bulkCustomMessage}
                onChange={e => {
                  if (e.target.value.length <= 500) {
                    setBulkCustomMessage(e.target.value);
                  }
                }}
                placeholder={
                  bulkReminderType === 'followup'
                    ? 'Type your follow-up message here — this will be sent to all selected applicants...'
                    : 'Add a personal note to include with this bulk reminder (optional)...'
                }
                rows={3}
                disabled={bulkSending}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white placeholder-stone-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm resize-none disabled:opacity-50"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-stone-400">
                  {bulkReminderType === 'followup'
                    ? 'Required — sent as the main message via email & WhatsApp.'
                    : 'Optional — appended to the standard reminder.'}
                </p>
                <span className={`text-xs font-medium ${bulkCustomMessage.length > 450 ? 'text-amber-400' : 'text-stone-400'}`}>
                  {bulkCustomMessage.length}/500
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Send Result */}
        {bulkSendResult && (
          <div className={`rounded-lg px-4 py-3 flex items-center gap-3 ${bulkSendResult.failed === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className={`text-sm ${bulkSendResult.failed === 0 ? 'ri-checkbox-circle-line text-emerald-600' : 'ri-alert-line text-amber-600'}`}></i>
            </div>
            <p className={`text-sm font-medium ${bulkSendResult.failed === 0 ? 'text-emerald-800' : 'text-amber-800'}`}>
              ✅ {bulkSendResult.success} sent successfully
              {bulkSendResult.failed > 0 && ` · ❌ ${bulkSendResult.failed} failed`}
            </p>
            <button onClick={() => setBulkSendResult(null)} className="ml-auto text-stone-400 hover:text-stone-600 cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-stone-200">
        <table className="w-full">
          <thead className={`border-b border-stone-200 ${activeSection === 'zoom' ? 'bg-stone-50' : 'bg-emerald-50'}`}>
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Time</th>
              {activeSection === 'zoom' && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Zoom Link</th>
              )}
              {activeSection === 'in-person' && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Venue</th>
              )}
              {viewMode === 'past' && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider min-w-[180px]">Attendance</th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider min-w-[220px]">Message & Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {displayedAppointments.map(app => {
              const currentReminderType = selectedReminder[app.id] || '24hr';
              const isSent = getReminderStatus(app, currentReminderType);
              const isSending = sendingReminder === app.id;
              const isPast = new Date(app.appointment_date) < now;
              const isChecked = selectedIds.has(app.id);
              const attendanceStatus = app.attendance_status || 'pending';

              return (
                <tr
                  key={app.id}
                  className={`hover:bg-stone-50 transition-colors align-top ${isPast ? 'opacity-90' : ''} ${isChecked ? 'bg-emerald-50/50' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSelect(app.id)}
                      className="w-4 h-4 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {isPast && attendanceStatus !== 'pending' && (
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${attendanceStatus === 'attended' ? 'bg-emerald-500' : 'bg-red-400'}`}></div>
                      )}
                      {isPast && attendanceStatus === 'pending' && (
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-history-line text-amber-500 text-xs"></i>
                        </div>
                      )}
                      <span className="text-sm font-medium text-stone-900">{app.applicant_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-0.5">
                      <p className="text-xs text-stone-600">{app.email}</p>
                      <a
                        href={`https://wa.me/${app.phone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline flex items-center gap-1"
                      >
                        <i className="ri-whatsapp-line"></i>
                        {app.phone}
                      </a>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-stone-900 font-medium">{formatDate(app.appointment_date)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-stone-400 text-sm"></i>
                      </div>
                      <span className="text-sm font-semibold text-stone-800 bg-stone-100 px-2 py-0.5 rounded-md whitespace-nowrap">
                        {formatTime(app.appointment_date)}
                      </span>
                    </div>
                  </td>
                  {activeSection === 'zoom' && (
                    <td className="px-4 py-3">
                      <a
                        href={app.zoom_link || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-stone-700 hover:text-stone-900 hover:underline bg-stone-100 px-2 py-1 rounded-md"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-video-line text-xs"></i>
                        </div>
                        Join Zoom
                      </a>
                    </td>
                  )}
                  {activeSection === 'in-person' && (
                    <td className="px-4 py-3 text-sm text-stone-600 max-w-[160px]">
                      <span className="line-clamp-2">{app.physical_address || '—'}</span>
                    </td>
                  )}
                  {/* Attendance column — only for past */}
                  {viewMode === 'past' && (
                    <td className="px-4 py-3">
                      <AttendanceBadge app={app} />
                    </td>
                  )}
                  {/* Message & Type */}
                  <td className="px-4 py-3 min-w-[260px]">
                    <select
                      value={currentReminderType}
                      onChange={e => setSelectedReminder(prev => ({ ...prev, [app.id]: e.target.value as ReminderType }))}
                      disabled={isSending}
                      className="w-full px-3 py-1.5 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm cursor-pointer bg-white mb-2"
                    >
                      <option value="24hr">⏰ 24-Hour Before</option>
                      <option value="evening">🌙 Evening Before</option>
                      <option value="2hr">⚡ 2-Hour Before</option>
                      <option value="anytime">📤 Send Anytime (General)</option>
                      <option value="followup">✍️ Custom Follow-Up</option>
                    </select>
                    {renderMessageComposer(app)}
                  </td>
                  <td className="px-4 py-3">
                    {currentReminderType === 'anytime' || currentReminderType === 'followup' ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                        <i className="ri-send-plane-line text-xs"></i> Always Available
                      </span>
                    ) : isSent ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                        <i className="ri-check-line text-xs"></i> Sent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-500 whitespace-nowrap">
                        <i className="ri-time-line text-xs"></i> Not Sent
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => sendManualReminder(app.id)}
                      disabled={isSending || bulkSending}
                      className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-white ${
                        currentReminderType === 'followup' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                    >
                      {isSending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className={`text-sm ${currentReminderType === 'followup' ? 'ri-message-2-line' : 'ri-send-plane-fill'}`}></i>
                          {currentReminderType === 'followup' ? 'Send Follow-Up' : viewMode === 'past' ? 'Send Follow-up' : `Send ${getReminderLabel(currentReminderType)}`}
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {displayedAppointments.length === 0 && (
          <div className="text-center py-14 text-stone-400">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-calendar-line text-4xl"></i>
            </div>
            <p className="font-medium text-stone-500">
              {searchQuery
                ? `No ${activeSection === 'zoom' ? 'Zoom' : 'In-Person'} appointments matching "${searchQuery}"`
                : `No ${viewMode} ${activeSection === 'zoom' ? 'Zoom' : 'In-Person'} appointments found.`}
            </p>
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {displayedAppointments.map(app => {
          const currentReminderType = selectedReminder[app.id] || '24hr';
          const isSent = getReminderStatus(app, currentReminderType);
          const isSending = sendingReminder === app.id;
          const isPast = new Date(app.appointment_date) < now;
          const isChecked = selectedIds.has(app.id);

          return (
            <div
              key={app.id}
              className={`bg-white rounded-lg border p-4 space-y-3 ${
                activeSection === 'zoom' ? 'border-l-4 border-l-stone-700' : 'border-l-4 border-l-emerald-600'
              } ${isPast ? 'opacity-90' : ''} ${isChecked ? 'ring-2 ring-emerald-400' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleSelect(app.id)}
                    className="w-4 h-4 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer mt-0.5"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      {isPast && (
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-history-line text-amber-500 text-xs"></i>
                        </div>
                      )}
                      <h3 className="font-semibold text-stone-900">{app.applicant_name}</h3>
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">{app.email}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                  app.is_lagos ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-800'
                }`}>
                  {app.is_lagos ? (
                    <><i className="ri-map-pin-line text-xs"></i> In-Person</>
                  ) : (
                    <><i className="ri-video-line text-xs"></i> Zoom</>
                  )}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-stone-50 rounded-lg p-2.5">
                  <p className="text-xs text-stone-400 mb-0.5 uppercase tracking-wide font-medium">Date</p>
                  <p className="text-stone-800 font-medium text-xs">{formatDate(app.appointment_date)}</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-2.5">
                  <p className="text-xs text-stone-400 mb-0.5 uppercase tracking-wide font-medium">Time</p>
                  <p className="text-stone-800 font-semibold text-sm">{formatTime(app.appointment_date)}</p>
                </div>
              </div>

              <div className="text-sm">
                <p className="text-xs text-stone-400 uppercase tracking-wide font-medium mb-1">
                  {app.is_lagos ? 'Venue' : 'Zoom Link'}
                </p>
                {app.is_lagos ? (
                  <p className="text-stone-700">{app.physical_address || '—'}</p>
                ) : (
                  <a
                    href={app.zoom_link || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-stone-700 hover:text-stone-900 hover:underline bg-stone-100 px-2 py-1 rounded-md text-xs"
                  >
                    <i className="ri-video-line"></i> Join Zoom Meeting
                  </a>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-emerald-500 text-sm"></i>
                </div>
                <a
                  href={`https://wa.me/${app.phone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:underline text-xs"
                >
                  {app.phone}
                </a>
              </div>

              {/* Attendance — mobile, past only */}
              {viewMode === 'past' && (
                <div className="pt-3 border-t border-stone-200">
                  <AttendanceBadge app={app} />
                </div>
              )}

              <div className="pt-3 border-t border-stone-200 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5 block">
                    Reminder / Message Type
                  </label>
                  <select
                    value={currentReminderType}
                    onChange={e => setSelectedReminder(prev => ({ ...prev, [app.id]: e.target.value as ReminderType }))}
                    disabled={isSending}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm cursor-pointer bg-white"
                  >
                    <option value="24hr">⏰ 24-Hour Before</option>
                    <option value="evening">🌙 Evening Before</option>
                    <option value="2hr">⚡ 2-Hour Before</option>
                    <option value="anytime">📤 Send Anytime (General)</option>
                    <option value="followup">✍️ Custom Follow-Up</option>
                  </select>
                </div>

                {renderMessageComposer(app)}

                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</span>
                  {currentReminderType === 'anytime' || currentReminderType === 'followup' ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      <i className="ri-send-plane-line text-xs"></i> Always Available
                    </span>
                  ) : isSent ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <i className="ri-check-line text-xs"></i> Sent
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-500">
                      <i className="ri-time-line text-xs"></i> Not Sent
                    </span>
                  )}
                </div>

                <button
                  onClick={() => sendManualReminder(app.id)}
                  disabled={isSending || bulkSending}
                  className={`w-full px-4 py-2.5 rounded-lg transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-white ${
                    currentReminderType === 'followup' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  {isSending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className={`text-sm ${currentReminderType === 'followup' ? 'ri-message-2-line' : 'ri-send-plane-fill'}`}></i>
                      {currentReminderType === 'followup' ? 'Send Follow-Up' : viewMode === 'past' ? 'Send Follow-up' : `Send ${getReminderLabel(currentReminderType)}`}
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
        {displayedAppointments.length === 0 && (
          <div className="text-center py-12 text-stone-400 bg-white rounded-lg border border-stone-200">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-calendar-line text-4xl"></i>
            </div>
            <p className="font-medium text-stone-500">
              {searchQuery
                ? `No appointments matching "${searchQuery}"`
                : `No ${viewMode} ${activeSection === 'zoom' ? 'Zoom' : 'In-Person'} appointments found.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
